import * as React from 'react';
import { Link } from 'gatsby';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import { getPathFromContentfulType } from '../../helpers/getPathFromContentfulType';
import { EmbeddedAsset } from './blocks';
import styles from './richtext.module.css';

type DocumentToReact = Parameters<typeof documentToReactComponents>;

interface RichTextProps {
  json: DocumentToReact[0] | null | undefined;
}

const options: DocumentToReact[1] = {
  renderNode: {
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
      console.log(node);

      return <p>Embedded entry block. {children}</p>;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      // console.log(node);

      return (
        <EmbeddedAsset contentfulId={node.data.target.sys.contentful_id} />
      );
    },
    [INLINES.EMBEDDED_ENTRY]: () => {
      // Currently unsupported. I don't see this being needed. Disabled in content types.
      // console.log(node);

      return null;
    },
    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      // console.log(node);
      const {
        fields,
        sys: {
          contentType: {
            sys: { id },
          },
        },
      } = node.data.target;

      let linkTo;

      switch (id) {
        case 'location': {
          linkTo = getPathFromContentfulType.location(fields.slug?.['en-US']);
          break;
        }
        case 'page': {
          linkTo = getPathFromContentfulType.page(fields.slug?.['en-US']);
          break;
        }
        case 'post': {
          linkTo = getPathFromContentfulType.post(fields.slug?.['en-US']);
          break;
        }
        case 'review': {
          linkTo = getPathFromContentfulType.review();
          break;
        }
        case 'serviceArea': {
          linkTo = getPathFromContentfulType.serviceArea(
            fields.slug?.['en-US']
          );
          break;
        }
        default: {
          linkTo = null;
          break;
        }
      }

      if (linkTo) {
        return <Link to={linkTo}>{children}</Link>;
      }

      return children;
    },
    [INLINES.HYPERLINK]: (node, children) => {
      // console.log(node);
      const { uri } = node.data;
      // Handle looking at the link and seeing if it's internal or not.
      // If internal use <Link />, else use <a> and set no referrer etc

      try {
        const url = new URL(uri);

        return (
          <a href={url.href} rel="noopener noreferrer">
            {children}
          </a>
        );
      } catch {
        return <Link to={uri}>{children}</Link>;
      }
    },
    [INLINES.ASSET_HYPERLINK]: (node, children) => {
      // console.log(node);

      const { fields } = node.data.target;

      if (fields) {
        // NOTE: `description` and `title` are available.
        const { file } = fields;

        return (
          <a
            href={file?.['en-US'].url}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {children}
          </a>
        );
      }

      return children;
    },
  },
};

export const RichText = ({ json }: RichTextProps) => {
  if (json) {
    const richText = documentToReactComponents(json, options);

    return <div className={styles.container}>{richText}</div>;
  }

  return null;
};
